import { useHistory, useParams } from 'react-router-dom';
import { get } from 'lodash';
import Alert from '@lendi-ui/alert';

import {
  ENTITY_TYPE,
  FIELD_PATHS,
  FIELD_VALUES,
  HEADING_TYPE,
  PERSON_TYPE,
  READONLY_ALERT_BODY,
  READONLY_ALERT_HEADING,
  REDUX_STORE,
  URL_PARAM_APPLICATION_ID,
  VISIT_CONTACT_TYPE,
} from '@funnels/constants';
import { useActiveRolesFromStore, useCustomerFormApplicant, useFormValues } from '@funnels/hooks';
import {
  getAccountantSelects,
  getApplicantSelects,
  getAssetAccountSelects,
  getConveyancerSelects,
  getLiabilityAccountSelects,
  getPropertySelects,
} from '@funnels/post-product-select/src/CustomerView/DynamicRouter/utils';
import {
  ActiveRoles,
  LiabilityContractsMap,
  PostProductSelectContract,
  RelatedParty,
  StoreApplicant,
  UseParams,
} from '@funnels/types';
import { AccordionGroup, FunnelGridUnit } from '@funnels/ui-components/Common';
import { Card } from '@funnels/ui-layouts/customer-view/_common';
import { getHeadingTypeBinary, getIndexFromId } from '@funnels/utils';

import {
  AccountantSummary,
  ApplicantSummary,
  AssetAccountDetailsSummary,
  ConveyancerSummary,
  LiabilityAccountDetailsSummary,
  PropertySummary,
} from './SummaryAccordion';

interface PostProductSelectSummaryProps {
  targetApplicantRole: FIELD_VALUES.BORROWER | FIELD_VALUES.GUARANTOR;
}

export const PostProductSelectSummary = ({ targetApplicantRole }: PostProductSelectSummaryProps) => {
  const [formValues] = useFormValues();
  const history = useHistory();
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = useParams<UseParams>();
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const assets = get(formValues, FIELD_PATHS.ASSETS(), []);
  const liabilities = get(formValues, FIELD_PATHS.LIABILITIES(), []);
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const employments = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);

  const customerApplicant = useCustomerFormApplicant();
  const roles: ActiveRoles | null = useActiveRolesFromStore();

  const hasReadOnly = Array.isArray(roles) && roles.includes(REDUX_STORE.ACTIVE_ROLES.ACTIVE_ROLES_VALUES.APP_READONLY);
  const customerRole = get(
    { applicants: [customerApplicant] },
    FIELD_PATHS.APPLICANT_TYPE(0),
    FIELD_VALUES.BORROWER as FIELD_VALUES.GUARANTOR | FIELD_VALUES.BORROWER
  );
  const postProductSelects: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);

  const filteredApplicants = applicants.filter(
    (applicant) => get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0)) === targetApplicantRole
  );

  const headingTypeBinary = getHeadingTypeBinary(customerRole, targetApplicantRole, filteredApplicants.length === 1);

  const labels: { [key: string]: string } = {
    [HEADING_TYPE.BORROWER_VIEWING_SINGLE_BORROWER]: `Additional details on your loan`,
    [HEADING_TYPE.BORROWER_VIEWING_MULTIPLE_BORROWER]: `Additional details on borrowers' loan`,
    [HEADING_TYPE.GUARANTOR_VIEWING_SINGLE_GUARANTOR]: `Additional details on your loan`,
    [HEADING_TYPE.GUARANTOR_VIEWING_MULTIPLE_GUARANTOR]: `Additional details on guarantors' loan`,
    [HEADING_TYPE.GUARANTOR_VIEWING_MULTIPLE_BORROWER]: `Additional details on borrowers' loan`,
    [HEADING_TYPE.GUARANTOR_VIEWING_SINGLE_BORROWER]: `Additional details on ${get(
      { applicants: filteredApplicants },
      FIELD_PATHS.APPLICANT_FIRST_NAME(0)
    )}'s loan`,
  };

  const heading = get(
    labels,
    headingTypeBinary,
    `You may not be permitted to view the additional details of ${targetApplicantRole}S`
  );

  // to ensure in the situation where the application is not locked,
  // we still need to check the roles and permission for the display logic of Edit button.
  const showEditButton =
    !hasReadOnly &&
    (
      [
        HEADING_TYPE.BORROWER_VIEWING_SINGLE_BORROWER,
        HEADING_TYPE.BORROWER_VIEWING_MULTIPLE_BORROWER,
        HEADING_TYPE.GUARANTOR_VIEWING_SINGLE_GUARANTOR,
        HEADING_TYPE.GUARANTOR_VIEWING_MULTIPLE_GUARANTOR,
        HEADING_TYPE.GUARANTOR_VIEWING_SINGLE_BORROWER,
        HEADING_TYPE.GUARANTOR_VIEWING_MULTIPLE_BORROWER,
      ] as string[]
    ).includes(headingTypeBinary);

  const applicantSelects = getApplicantSelects(filteredApplicants, postProductSelects);
  const propertySelects = getPropertySelects(postProductSelects);
  const conveyancerSelects = getConveyancerSelects(postProductSelects);
  const accountantSelects = getAccountantSelects(postProductSelects);
  const assetAccountSelects = getAssetAccountSelects(postProductSelects);
  const liabilityAccountContracts: LiabilityContractsMap = getLiabilityAccountSelects(postProductSelects);

  function handleEditClick(subRoute: string, editApplicantId?: string) {
    editApplicantId
      ? history.push(
          `/application/${applicationId}/post-product-select/summary-edit/${targetApplicantRole.toLowerCase()}/${subRoute}/${editApplicantId}`
        )
      : history.push(
          `/application/${applicationId}/post-product-select/summary-edit/${targetApplicantRole.toLowerCase()}/${subRoute}`
        );
  }

  return (
    <Card heading={heading} actionCustomText="Done">
      {hasReadOnly && (
        <FunnelGridUnit my="sm" data-component="readonly-alert">
          <Alert variant="info" heading={READONLY_ALERT_HEADING}>
            {READONLY_ALERT_BODY}
          </Alert>
        </FunnelGridUnit>
      )}
      {
        // Do not allow the borrowers to view the guarantors' pps summary,
        (customerRole === FIELD_VALUES.GUARANTOR || customerRole === targetApplicantRole) && (
          <AccordionGroup>
            {applicantSelects.map((applicantSelect) => {
              const applicantId = get(
                { postProductSelect: [applicantSelect] },
                FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
              );
              const applicantIndex = getIndexFromId(applicants, applicantId);
              const relatedPartyIndex = relatedParties.findIndex(
                (relatedParty) =>
                  get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === applicantId &&
                  get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === PERSON_TYPE.Spouse
              );
              return applicantIndex !== -1 ? (
                <ApplicantSummary
                  applicantIndex={applicantIndex}
                  relatedPartyIndex={relatedPartyIndex}
                  applicantSelect={applicantSelect}
                  key={`applicant-${applicantIndex}`}
                  handleEditClick={showEditButton ? () => handleEditClick('applicant', applicantId) : undefined}
                />
              ) : null;
            })}
            {propertySelects.map((propertySelect) => {
              const assetId = get(
                { postProductSelect: [propertySelect] },
                FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
              );
              const assetIndex = getIndexFromId(assets, assetId);
              const relatedPartyIndex = relatedParties.findIndex(
                (relatedParty) =>
                  get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_ASSET_ID(0)) === assetId &&
                  Object.values(VISIT_CONTACT_TYPE).includes(
                    get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0))
                  )
              );
              return assetIndex !== -1 ? (
                <PropertySummary
                  assetIndex={assetIndex}
                  relatedPartyIndex={relatedPartyIndex}
                  propertySelect={propertySelect}
                  key={`asset-${assetIndex}`}
                  handleEditClick={showEditButton ? () => handleEditClick('property') : undefined}
                />
              ) : null;
            })}
            {conveyancerSelects.map((conveyancerSelect) => {
              const assetId = get(
                { postProductSelect: [conveyancerSelect] },
                FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
              );
              const assetIndex = getIndexFromId(assets, assetId);
              const relatedPartyIndex = relatedParties.findIndex(
                (relatedParty) =>
                  get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === ENTITY_TYPE.SOLICITOR
              );
              return assetIndex !== -1 ? (
                <ConveyancerSummary
                  relatedPartyIndex={relatedPartyIndex}
                  conveyancerSelect={conveyancerSelect}
                  key={`conveyancer-${relatedPartyIndex}`}
                  handleEditClick={showEditButton ? () => handleEditClick('conveyancer') : undefined}
                />
              ) : null;
            })}
            {accountantSelects.map((accountantSelect) => {
              const employmentId = get(
                { postProductSelect: [accountantSelect] },
                FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
              );
              const employmentIndex = getIndexFromId(employments, employmentId);
              const employmentApplicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
              const relatedPartyIndex = relatedParties.findIndex(
                (relatedParty) =>
                  get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_EMPLOYMENT_ID(0)) === employmentId
              );
              return employmentIndex !== -1 && !!employmentApplicantId ? (
                <AccountantSummary
                  relatedPartyIndex={relatedPartyIndex}
                  accountantSelect={accountantSelect}
                  key={`employment-${employmentIndex}`}
                  handleEditClick={
                    showEditButton ? () => handleEditClick('accountant', employmentApplicantId) : undefined
                  }
                />
              ) : null;
            })}
            {assetAccountSelects.map((assetAccountSelect) => {
              const assetId = get(
                { postProductSelect: [assetAccountSelect] },
                FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
              );
              const assetIndex = getIndexFromId(assets, assetId);
              return assetIndex !== -1 ? (
                <AssetAccountDetailsSummary
                  assetIndex={assetIndex}
                  key={`assetAccountDetails-${assetIndex}`}
                  handleEditClick={showEditButton ? () => handleEditClick('account-details') : undefined}
                />
              ) : null;
            })}
            {Object.entries(liabilityAccountContracts).map(([liabilityId, contracts]) => {
              const liabilityIndex = getIndexFromId(liabilities, liabilityId);
              const { liabilityAccountContract, liabilityContract } = contracts;
              return liabilityIndex !== -1 ? (
                <LiabilityAccountDetailsSummary
                  liabilityIndex={liabilityIndex}
                  liabilityAccountContract={liabilityAccountContract}
                  liabilityContract={liabilityContract}
                  key={`liabilityAccountDetails-${liabilityIndex}`}
                  handleEditClick={showEditButton ? () => handleEditClick('account-details') : undefined}
                />
              ) : null;
            })}
          </AccordionGroup>
        )
      }
    </Card>
  );
};
